import React, { useEffect, useRef, useMemo } from 'react';
import type { InstrumentType } from '../../types/composition';
import { trapFocus, focusFirstElement, announceToScreenReader } from '../../utils/accessibility';
import { instrumentDatabase } from '../../services/instruments/InstrumentDatabase';
import { CloseButton } from '../common/CloseButton';
import './TracksPanel.css';

/**
 * Track information for display in the panel
 */
export interface TrackInfo {
  /** Track index (0-based) */
  index: number;
  /** Track name/label */
  name: string;
  /** Instrument type */
  instrument: InstrumentType;
  /** Whether track is muted */
  muted: boolean;
  /** Whether track is soloed */
  solo: boolean;
}

/**
 * Props for TracksPanel component
 * 
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 16.2, 16.3, 16.5
 */
export interface TracksPanelProps {
  /** Array of all tracks in the composition */
  tracks: TrackInfo[];
  /** Currently selected track index */
  selectedTrackIndex: number;
  /** Callback when a track is selected */
  onTrackSelect: (index: number) => void;
  /** Callback when mute is toggled for a track */
  onMuteToggle: (index: number) => void;
  /** Callback when solo is toggled for a track */
  onSoloToggle: (index: number) => void;
  /** Callback when add track button is clicked */
  onAddTrack: () => void;
  /** Callback when panel is closed */
  onClose: () => void;
}

/**
 * TracksPanel Component
 * 
 * Full-screen panel for viewing and managing all tracks in the composition.
 * Displays list of all tracks with name, instrument, Mute and Solo buttons.
 * Each track row is tappable to select that track.
 * Provides "Add Track" button at bottom and close button in header.
 * 
 * Requirements: 7.1, 7.2, 7.3, 7.4, 7.5, 7.6, 7.7, 16.2, 16.3, 16.5
 */
export const TracksPanel: React.FC<TracksPanelProps> = ({
  tracks,
  selectedTrackIndex,
  onTrackSelect,
  onMuteToggle,
  onSoloToggle,
  onAddTrack,
  onClose,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Create a map of instrument IDs to display names
  const instrumentDisplayNames = useMemo(() => {
    const map = new Map<InstrumentType, string>();
    tracks.forEach(track => {
      if (!map.has(track.instrument)) {
        const metadata = instrumentDatabase.getInstrument(track.instrument);
        map.set(track.instrument, metadata?.displayName || track.instrument);
      }
    });
    return map;
  }, [tracks]);

  /**
   * Get display name for an instrument
   */
  const getInstrumentDisplayName = (instrument: InstrumentType): string => {
    return instrumentDisplayNames.get(instrument) || instrument;
  };

  /**
   * Set up focus trap and announce panel opening
   * Requirements 16.3, 16.5: Keyboard navigation and screen reader announcements
   */
  useEffect(() => {
    announceToScreenReader('Tracks panel opened');
    
    if (panelRef.current) {
      focusFirstElement(panelRef.current);
      const cleanup = trapFocus(panelRef.current);
      return cleanup;
    }
    
    return undefined;
  }, []);

  /**
   * Handle track row click - select track and close panel
   * Requirement 7.4: Tapping track row selects that track and returns to Grid tab
   */
  const handleTrackRowClick = (index: number) => {
    const track = tracks.find(t => t.index === index);
    if (track) {
      announceToScreenReader(`Selected ${track.name}`);
    }
    onTrackSelect(index);
  };

  /**
   * Handle mute button click - prevent event propagation to avoid selecting track
   * Requirement 7.3: Display Mute button for each track
   */
  const handleMuteClick = (e: React.MouseEvent | React.KeyboardEvent, index: number) => {
    e.stopPropagation();
    const track = tracks.find(t => t.index === index);
    if (track) {
      announceToScreenReader(`${track.name} ${track.muted ? 'unmuted' : 'muted'}`);
    }
    onMuteToggle(index);
  };

  /**
   * Handle solo button click - prevent event propagation to avoid selecting track
   * Requirement 7.3: Display Solo button for each track
   */
  const handleSoloClick = (e: React.MouseEvent | React.KeyboardEvent, index: number) => {
    e.stopPropagation();
    const track = tracks.find(t => t.index === index);
    if (track) {
      announceToScreenReader(`${track.name} ${track.solo ? 'unsoloed' : 'soloed'}`);
    }
    onSoloToggle(index);
  };

  /**
   * Handle keyboard shortcuts
   * Requirement 16.3: Keyboard navigation support
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop overlay - Requirement 7.1: Full-screen overlay */}
      <div 
        className="tracks-panel-backdrop" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Panel container - Requirement 7.1: Full-screen sheet */}
      <div 
        ref={panelRef}
        className="tracks-panel" 
        role="dialog" 
        aria-label="Tracks panel"
        aria-modal="true"
        onKeyDown={handleKeyDown}
      >
        {/* Header with close button - Requirement 7.7 */}
        <div className="tracks-panel-header">
          <h2 className="tracks-panel-title">Tracks</h2>
          <CloseButton
            onClick={onClose}
            ariaLabel="Close tracks panel"
            size="medium"
            variant="default"
          />
        </div>

        {/* Scrollable track list - Requirement 7.2 */}
        <div className="tracks-panel-content">
          <div className="tracks-panel-list" role="list">
            {tracks.map((track) => (
              <div
                key={track.index}
                className={`track-row ${selectedTrackIndex === track.index ? 'selected' : ''}`}
                onClick={() => handleTrackRowClick(track.index)}
                role="listitem"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTrackRowClick(track.index);
                  }
                }}
                aria-label={`Select ${track.name} - ${getInstrumentDisplayName(track.instrument)}`}
                aria-current={selectedTrackIndex === track.index ? 'true' : undefined}
              >
                {/* Track info */}
                <div className="track-row-info">
                  <div className="track-row-name">{track.name}</div>
                  <div className="track-row-instrument">
                    {getInstrumentDisplayName(track.instrument)}
                  </div>
                </div>

                {/* Track controls - Requirement 7.3 */}
                <div className="track-row-controls">
                  {/* Mute button */}
                  <button
                    className={`track-control-btn track-control-mute ${track.muted ? 'active' : ''}`}
                    onClick={(e) => handleMuteClick(e, track.index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleMuteClick(e, track.index);
                      }
                    }}
                    aria-label={track.muted ? 'Unmute track' : 'Mute track'}
                    aria-pressed={track.muted}
                    title={track.muted ? 'Unmute' : 'Mute'}
                  >
                    <i className={`fa-solid ${track.muted ? 'fa-volume-xmark' : 'fa-volume-high'}`} aria-hidden="true"></i>
                  </button>

                  {/* Solo button */}
                  <button
                    className={`track-control-btn track-control-solo ${track.solo ? 'active' : ''}`}
                    onClick={(e) => handleSoloClick(e, track.index)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleSoloClick(e, track.index);
                      }
                    }}
                    aria-label={track.solo ? 'Unsolo track' : 'Solo track'}
                    aria-pressed={track.solo}
                    title={track.solo ? 'Unsolo' : 'Solo'}
                  >
                    <i className="fa-solid fa-headphones" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer with Add Track button - Requirement 7.5, 7.6 */}
        <div className="tracks-panel-footer">
          <button
            className="tracks-panel-add-track"
            onClick={onAddTrack}
            aria-label="Add new track"
            title="Add Track"
          >
            <i className="fa-solid fa-plus" aria-hidden="true"></i>
            <span>Add Track</span>
          </button>
        </div>
      </div>
    </>
  );
};
